export enum CatLifeStageKey {
  Kitten = "kitten",
  YoungAdult = "youngAdult",
  MatureAdult = "matureAdult",
  Senior = "senior",
}

export interface CatLifeStage {
  key: CatLifeStageKey;
  label: string;
  ageRangeLabel: string;
}

export interface AgeConversionRow {
  label: string;
  totalMonths: number;
  humanAge: number;
  lifeStage: CatLifeStage;
}

export function catAgeToTotalMonths(years: number, months: number): number {
  return years * 12 + months;
}

/**
 * International Cat Care による月齢別の人間換算年齢の基準点。
 * 0〜24ヶ月の間はこれらの点を結ぶ線分上を線形補間する。
 * @see https://icatcare.org/articles/how-to-tell-your-cats-age-in-human-years
 */
const ICC_HUMAN_AGE_ANCHORS: readonly {
  catMonths: number;
  humanYears: number;
}[] = [
  { catMonths: 0, humanYears: 0 },
  { catMonths: 1, humanYears: 1 },
  { catMonths: 2, humanYears: 2 },
  { catMonths: 3, humanYears: 4 },
  { catMonths: 4, humanYears: 6 },
  { catMonths: 5, humanYears: 8 },
  { catMonths: 6, humanYears: 10 },
  { catMonths: 7, humanYears: 12 },
  { catMonths: 12, humanYears: 15 },
  { catMonths: 18, humanYears: 21 },
  { catMonths: 24, humanYears: 24 },
];

export function convertCatAgeToHumanAge(totalMonths: number): number {
  const lastAnchor = ICC_HUMAN_AGE_ANCHORS[ICC_HUMAN_AGE_ANCHORS.length - 1];
  if (totalMonths >= lastAnchor.catMonths) {
    return Math.round(
      lastAnchor.humanYears + (4 * (totalMonths - lastAnchor.catMonths)) / 12,
    );
  }

  const upperIndex = ICC_HUMAN_AGE_ANCHORS.findIndex(
    (anchor) => anchor.catMonths >= totalMonths,
  );
  const upper = ICC_HUMAN_AGE_ANCHORS[upperIndex];
  const lower = ICC_HUMAN_AGE_ANCHORS[upperIndex - 1];
  if (!lower) {
    return upper.humanYears;
  }

  const ratio =
    (totalMonths - lower.catMonths) / (upper.catMonths - lower.catMonths);
  return Math.round(
    lower.humanYears + ratio * (upper.humanYears - lower.humanYears),
  );
}

export const CAT_LIFE_STAGES: readonly CatLifeStage[] = [
  { key: CatLifeStageKey.Kitten, label: "子猫期", ageRangeLabel: "〜1歳" },
  {
    key: CatLifeStageKey.YoungAdult,
    label: "成猫期",
    ageRangeLabel: "1〜6歳",
  },
  {
    key: CatLifeStageKey.MatureAdult,
    label: "中高年期",
    ageRangeLabel: "7〜10歳",
  },
  { key: CatLifeStageKey.Senior, label: "シニア期", ageRangeLabel: "11歳〜" },
];

const lifeStageByKey = new Map(
  CAT_LIFE_STAGES.map((stage) => [stage.key, stage]),
);

function lifeStageOf(key: CatLifeStageKey): CatLifeStage {
  const stage = lifeStageByKey.get(key);
  if (!stage) {
    throw new Error(`unknown life stage key: ${key}`);
  }
  return stage;
}

export const SENIOR_STAGE_START_YEARS = 11;

export function getCatLifeStage(totalMonths: number): CatLifeStage {
  if (totalMonths < 12) {
    return lifeStageOf(CatLifeStageKey.Kitten);
  }
  if (totalMonths <= 83) {
    return lifeStageOf(CatLifeStageKey.YoungAdult);
  }
  if (totalMonths < SENIOR_STAGE_START_YEARS * 12) {
    return lifeStageOf(CatLifeStageKey.MatureAdult);
  }
  return lifeStageOf(CatLifeStageKey.Senior);
}

export const CAT_AGE_GUIDE_YEARS: readonly number[] = Array.from(
  { length: 20 },
  (_, index) => index + 1,
);

export function buildAgeConversionTable(): AgeConversionRow[] {
  const monthRows = [3, 6].map((months) => ({
    label: `${months}ヶ月`,
    totalMonths: months,
  }));
  const yearRows = CAT_AGE_GUIDE_YEARS.map((years) => ({
    label: `${years}歳`,
    totalMonths: years * 12,
  }));

  return [...monthRows, ...yearRows].map((row) => ({
    ...row,
    humanAge: convertCatAgeToHumanAge(row.totalMonths),
    lifeStage: getCatLifeStage(row.totalMonths),
  }));
}

export function isCatAgeGuideYears(years: number): boolean {
  return CAT_AGE_GUIDE_YEARS.includes(years);
}

export function parseCatAgeGuideYears(value: unknown): number | null {
  const parsed = Number(value);
  if (!Number.isInteger(parsed) || !isCatAgeGuideYears(parsed)) {
    return null;
  }
  return parsed;
}

export function humanAgeOfYears(years: number): number {
  return convertCatAgeToHumanAge(years * 12);
}
